import type { ProductImageEntry } from "./types";

/** Read-only capture copy for resource forms and the setup guide. */
export const RESOURCE_MORE_DETAIL_CATALOGUE: readonly ProductImageEntry[] = [
  {
    id: "standing-data.client-edit-basics",
    module: "Kunduppgifter",
    area: "standing-data",
    opens: "Resurser → klient → Redigera → Grunder",
    setup: ["Öppna en befintlig klients grunduppgifter"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Klientens identitet och besöksadress i redigeringsformuläret",
        shows:
          "Formuläret samlar klientens namn, kontaktuppgifter och besöksadress.",
        proves:
          "Befintliga klientuppgifter kan granskas i sitt sammanhang innan någon ändring görs.",
      },
      en: {
        alt: "Client identity and visiting address in the edit form",
        shows:
          "The form brings together the client's name, contact details and visiting address.",
        proves:
          "Existing client details can be reviewed in context before anyone makes a change.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.client-edit-advanced-planning",
    module: "Kontinuitet och planeringsstandarder",
    area: "standing-data",
    opens: "Resurser → klient → Redigera → Avancerat",
    setup: ["Öppna klientens kontinuitets- och standardinställningar"],
    scenario: "S5",
    visual: { variant: 5, focus: "client-continuity-fields" },
    text: {
      sv: {
        alt: "Klientens kontinuitetsgräns och standardvärden för besök",
        shows:
          "Avancerade klientfält samlar kontinuitetsmål och standardvärden som kan ärvas av nya besök.",
        proves:
          "Formuläret visar var planeringsvärden granskas och hur klientens värden hålls åtskilda från enhetens standarder.",
      },
      en: {
        alt: "Client continuity limit and default visit settings",
        shows:
          "Advanced client fields bring together continuity targets and defaults that new visits can inherit.",
        proves:
          "The form shows where planning values are reviewed and how client values remain distinct from unit defaults.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.care-plan-recurrence",
    module: "Besöksfrekvens",
    area: "standing-data",
    opens: "Resurser → klient → Genomförandeplan → Redigera besök → Grunder",
    setup: ["Öppna en befintlig besöksmall"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Besöksmallens sparade frekvens och veckodagar",
        shows:
          "Frekvenssteget visar den sparade återkomsten och, när den används, valda veckodagar.",
        proves:
          "Återkommande besök granskas utifrån klientens befintliga plan innan mallen redigeras.",
      },
      en: {
        alt: "Saved recurrence and weekdays in a visit template",
        shows:
          "The frequency step shows the saved recurrence and, when used, selected weekdays.",
        proves:
          "Recurring visits can be reviewed against the client's existing plan before editing the template.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.care-plan-time-windows",
    module: "Besökstid och tidsfönster",
    area: "standing-data",
    opens: "Resurser → klient → Genomförandeplan → Redigera besök → Grunder",
    setup: ["Öppna besöksmallens befintliga varaktighet och tidsfönster"],
    scenario: "S5",
    visual: { variant: 5, focus: "time-window-panel" },
    text: {
      sv: {
        alt: "Besöksmallens varaktighet och sparade tidsfönster",
        shows:
          "Formuläret visar besökets längd, önskad start och tillåtet tidsfönster.",
        proves:
          "Planerad tid och flexibilitet kan läsas direkt tillsammans i samma besöksmall.",
      },
      en: {
        alt: "Visit duration and saved time windows in a template",
        shows:
          "The form shows visit duration, preferred start and the allowed time window.",
        proves:
          "Planned time and flexibility can be reviewed together in the same visit template.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.care-plan-skills",
    module: "Besöksmallens kompetenskrav",
    area: "standing-data",
    opens: "Resurser → klient → Genomförandeplan → Redigera besök → Valfritt",
    setup: ["Öppna besöksmallens kompetenssteg"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Kompetenser kopplade till en befintlig besöksmall",
        shows:
          "Kompetenssteget visar mallens valda kompetenser och den tillgängliga kompetenskatalogen.",
        proves:
          "Kompetenskrav granskas på besöksmallen där de påverkar vem som kan utföra besöket.",
      },
      en: {
        alt: "Competencies attached to an existing visit template",
        shows:
          "The competency step shows selected template competencies and the available competency catalogue.",
        proves:
          "Competency requirements are reviewed on the visit template where they affect who can perform the visit.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.care-plan-double-staffing",
    module: "Dubbelbemanning i besöksmall",
    area: "standing-data",
    opens: "Resurser → klient → Genomförandeplan → Redigera besök → Grunder",
    setup: [
      "Granska besöksmallens inställning för dubbelbemanning utan att slå om den",
    ],
    scenario: "S5",
    visual: { variant: 5, focus: "double-staffing-setting" },
    text: {
      sv: {
        alt: "Besöksmallens befintliga inställning för dubbelbemanning",
        shows:
          "Besöksmallen visar om besöket kräver två medarbetare och hur en eventuell partner är kopplad.",
        proves:
          "Behovet av samtidig bemanning syns på besöket där inställningen hör hemma.",
      },
      en: {
        alt: "Existing double-staffing setting on a visit template",
        shows:
          "The template shows whether a visit requires two staff and how an existing partner is linked.",
        proves:
          "The need for simultaneous staffing is visible on the visit where that setting belongs.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.employee-edit-identity",
    module: "Medarbetaruppgifter",
    area: "standing-data",
    opens:
      "Resurser → enhet → Medarbetare → öppna medarbetare → Redigera → Grunder",
    setup: ["Öppna den första befintliga medarbetaren från listan"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Medarbetarens identitet och enhet i redigeringsformuläret",
        shows:
          "Grundsteget samlar medarbetarens identitet, kontakt och tillhörighet till enhet.",
        proves:
          "Befintliga personaluppgifter kan granskas på samma sida innan de ändras.",
      },
      en: {
        alt: "Employee identity and service area in the edit form",
        shows:
          "The basics step brings together employee identity, contact and service-area assignment.",
        proves:
          "Existing staff details can be reviewed on one page before they are changed.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.employee-edit-transport",
    module: "Anställning och transportsätt",
    area: "standing-data",
    opens:
      "Resurser → enhet → Medarbetare → öppna medarbetare → Redigera → Avancerat",
    setup: [
      "Öppna medarbetarens befintliga anställnings- och kapacitetsuppgifter",
    ],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Medarbetarens anställningskapacitet och transportsätt",
        shows:
          "Avancerade uppgifter samlar avtalsramar, kapacitet och transportsätt.",
        proves:
          "Planeringsförutsättningar för personal granskas i medarbetarens befintliga formulär.",
      },
      en: {
        alt: "Employee contract capacity and transport modes",
        shows:
          "Advanced details bring together contract limits, capacity and transport modes.",
        proves:
          "Staff planning inputs can be reviewed in the employee's existing form.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.employee-edit-availability",
    module: "Medarbetarens tillgänglighet",
    area: "standing-data",
    opens:
      "Resurser → enhet → Medarbetare → öppna medarbetare → Redigera → Avancerat → Veckoschema",
    setup: ["Visa medarbetarens sparade ordinarie arbetspass"],
    scenario: "S5",
    visual: { variant: 5, focus: "employee-weekly-roster" },
    text: {
      sv: {
        alt: "Sparade arbetspass i medarbetarens veckoschema",
        shows:
          "Veckoschemat visar medarbetarens ordinarie pass och sammanfattad arbetstid.",
        proves:
          "Återkommande tillgänglighet kan granskas i kalendern innan schemat ändras.",
      },
      en: {
        alt: "Saved shifts in the employee's weekly schedule",
        shows:
          "The weekly schedule shows the employee's ordinary shifts and a summary of working hours.",
        proves:
          "Recurring availability can be reviewed in the calendar before the schedule is changed.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.employee-edit-skills",
    module: "Medarbetarens kompetenser",
    area: "standing-data",
    opens:
      "Resurser → enhet → Medarbetare → öppna medarbetare → Redigera → Avancerat → Planeringsregler",
    setup: ["Öppna medarbetarens befintliga kompetenser"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Valda kompetenser i medarbetarens planeringsregler",
        shows:
          "Kompetensdelen visar medarbetarens valda kompetenser och taggar.",
        proves:
          "Personalens befintliga kompetenser kan jämföras med kraven på besöksmallar.",
      },
      en: {
        alt: "Selected competencies in the employee's planning rules",
        shows:
          "The competency section shows the employee's selected competencies and tags.",
        proves:
          "Existing staff competencies can be reviewed alongside visit-template requirements.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.employee-edit-costs",
    module: "Medarbetarens timkostnad",
    area: "standing-data",
    opens:
      "Resurser → enhet → Medarbetare → öppna medarbetare → Redigera → Avancerat → Lön och timkostnad",
    setup: ["Öppna en befintlig kostnadsrad med läsbehörighet"],
    scenario: "S5",
    visual: { variant: 5, focus: "employee-costs-table" },
    text: {
      sv: {
        alt: "Befintliga löne- och timkostnadsrader för en medarbetare",
        shows:
          "Kostnadstabellen visar sparade timsatser och deras giltighetstid.",
        proves:
          "Kostnadsuppgifter kan granskas där de kopplas till medarbetaren, med behörigheten intakt.",
      },
      en: {
        alt: "Existing salary and hourly-cost records for an employee",
        shows:
          "The cost table shows saved hourly rates and their effective dates.",
        proves:
          "Cost details can be reviewed where they belong to the employee, subject to existing permissions.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.unit-edit-basics",
    module: "Enhetens identitet och plats",
    area: "standing-data",
    opens: "Resurser → enhet → Redigera → Grunder",
    setup: ["Öppna den befintliga syntetiska Nacka-enhetens grunduppgifter"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Enhetens namn och plats i redigeringsformuläret",
        shows: "Grundsteget samlar enhetens namn, hierarki och depåadress.",
        proves:
          "Enhetens befintliga planeringskontext granskas på en egen resursvy.",
      },
      en: {
        alt: "Service-area name and location in the edit form",
        shows:
          "The basics step brings together the unit name, hierarchy and depot address.",
        proves:
          "The unit's existing planning context can be reviewed on its own resource page.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "standing-data.unit-edit-demand",
    module: "Enhetens efterfrågan och besöksstandarder",
    area: "standing-data",
    opens: "Resurser → enhet → Redigera → Avancerat → Efterfrågan och besök",
    setup: ["Öppna enhetens befintliga standarder för besök och kontinuitet"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Enhetens standardvärden för besöksprioritet och kontinuitet",
        shows:
          "Efterfrågan och besök visar standardvärden som kan ärvas av nya klientbesök.",
        proves:
          "Enhetens planeringsstandarder går att granska separat från klienternas överstyringar.",
      },
      en: {
        alt: "Unit defaults for visit priority and continuity",
        shows:
          "Demand and visits shows defaults that new client visits can inherit.",
        proves:
          "Unit planning defaults can be reviewed separately from client overrides.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "onboarding.start-checklist",
    module: "Kom igång-guiden",
    area: "standing-data",
    opens: "/start",
    setup: ["Öppna setup-guidens checklista utan att ändra uppgiftsstatus"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Kom igång-checklista med organisationens setup-steg",
        shows:
          "Guiden samlar setup-uppgifter, aktuell progress och kraven för den första schemaplanen.",
        proves:
          "Planeraren kan se nästa grundläggande datasteg utan att lämna den löpande arbetsytan.",
      },
      en: {
        alt: "Get-started checklist with the organisation's setup steps",
        shows:
          "The guide brings together setup tasks, current progress and the requirements for a first schedule.",
        proves:
          "Planners can review the next foundation step without leaving the working application.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "onboarding.operational-settings",
    module: "Grundinställningar för verksamheten",
    area: "standing-data",
    opens: "/start/settings",
    setup: [
      "Granska befintliga arbetspassmallar, insatser, roller och kompetenser utan att bekräfta eller ändra",
    ],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Verksamhetens arbetspassmallar, insatser, roller och kompetenser",
        shows:
          "Inställningssteget sammanfattar verksamhetens befintliga grundkataloger.",
        proves:
          "Planeraren kan kontrollera operativa standarder innan de öppnar respektive inställning för ändring.",
      },
      en: {
        alt: "Operational shift templates, visit types, roles and competencies",
        shows:
          "The settings step summarises the organisation's existing foundation catalogues.",
        proves:
          "Planners can review operational defaults before opening an individual setting to change it.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
];
